import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router";
import { ApiError } from "./lib/api";
import { ConfirmProvider } from "./lib/confirm";
import { I18nProvider } from "./lib/i18n";
import { ToastProvider } from "./lib/toast";
import { applyTheme } from "./lib/theme";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { router } from "./router";
import "./styles.css";

applyTheme();
// The dock offers its own "add to home screen" button (features/dock/install.ts); keep Chrome's prompt for it.
window.addEventListener("beforeinstallprompt", (e) => {
  if (!window.location.pathname.startsWith("/dock")) return;
  e.preventDefault();
  (window as Window & { __installPrompt?: Event }).__installPrompt = e;
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      retry: (count, e) => !(e instanceof ApiError && e.status > 0 && e.status < 500) && count < 2,
    },
  },
});

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  import("virtual:pwa-register").then(({ registerSW }) => registerSW({ immediate: true })).catch(() => {});
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <I18nProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <ConfirmProvider>
              <RouterProvider router={router} />
            </ConfirmProvider>
          </ToastProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </I18nProvider>
  </StrictMode>,
);
